import { useEffect, useState } from 'react';

import goldCoin from '../../assets/goldCoin.gif';
import greenCoin from '../../assets/greenCoin.gif';

import { FADE_IN_DURATION_MS, TRANSITION_DURATION_MS } from './constants';
import { FlyingCoins } from './FlyingCoins';
import type { CoinAnimationData } from './types';

type FlyingCoinsAnimationProps = {
  onEnd?: () => void;
  isGCEnd?: () => void;
  isSCEnd?: () => void;
  Gc: CoinAnimationData;
  Sc: CoinAnimationData;
  fadeInDurationMs?: number;
  transitionDurationMs?: number;
};

export function FlyingCoinsAnimation({
  onEnd,
  isGCEnd,
  isSCEnd,
  Gc,
  Sc,
  fadeInDurationMs = FADE_IN_DURATION_MS,
  transitionDurationMs = TRANSITION_DURATION_MS,
}: FlyingCoinsAnimationProps) {
  const [state, setState] = useState({
    firstDone: Gc.amount === 0,
    gcSpot: { x: 0, y: 0 },
    scSpot: { x: 0, y: 0 },
  });

  const {
    landingSpotCords: gcLandingSpotCords,
    landingOffsetX: gcLandingOffsetX,
    landingOffsetY: gcLandingOffsetY,
  } = Gc;
  const {
    landingSpotCords: scLandingSpotCords,
    landingOffsetX: scLandingOffsetX,
    landingOffsetY: scLandingOffsetY,
  } = Sc;

  const { x: gcLandingSpotCordsX, y: gcLandingSpotCordsY } = gcLandingSpotCords;
  const { x: scLandingSpotCordsX, y: scLandingSpotCordsY } = scLandingSpotCords;

  useEffect(() => {
    document.documentElement.style.setProperty('--keyframe-duration', `${fadeInDurationMs / 1000}s`);
    document.documentElement.style.setProperty('--transition-duration', `${transitionDurationMs / 1000}s`);

    setState((prev) => ({
      ...prev,
      gcSpot: {
        x: gcLandingSpotCordsX + gcLandingOffsetX,
        y: gcLandingSpotCordsY + gcLandingOffsetY,
      },
      scSpot: {
        x: scLandingSpotCordsX + scLandingOffsetX,
        y: scLandingSpotCordsY + scLandingOffsetY,
      },
    }));
  }, [
    gcLandingOffsetX,
    gcLandingOffsetY,
    gcLandingSpotCordsX,
    gcLandingSpotCordsY,
    scLandingOffsetX,
    scLandingOffsetY,
    scLandingSpotCordsX,
    scLandingSpotCordsY,
    fadeInDurationMs,
    transitionDurationMs,
  ]);

  const screenCenter = {
    x: window.innerWidth / 2,
    y: window.innerHeight / 2,
  };

  const commonProps = {
    fadeInDurationMs,
    transitionDurationMs,
    start: screenCenter,
  };

  const handleAnimationEnd = () => {
    if (Sc.amount) {
      setState((prev) => ({ ...prev, firstDone: true }));
    } else if (onEnd) {
      onEnd();
    }
  };

  return (
    <div>
      {!state.firstDone ? (
        <FlyingCoins
          {...commonProps}
          key="GC"
          amount={Gc.amount}
          coinType="GC"
          end={state.gcSpot}
          gif={goldCoin}
          onEnd={handleAnimationEnd}
          onLandingCoin={() => isGCEnd?.()}
        />
      ) : (
        <FlyingCoins
          {...commonProps}
          key="SC"
          amount={Sc.amount}
          coinType="SC"
          end={state.scSpot}
          gif={greenCoin}
          onEnd={() => onEnd?.()}
          onLandingCoin={() => isSCEnd?.()}
        />
      )}
    </div>
  );
}
